import { useState, useEffect, useCallback } from 'react';
import { FileText, Upload, Image, RefreshCw, Trash2, Crown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fiyat listesi kategorileri
const PRICE_LIST_CATEGORIES = [
  { id: 'dental-gbp', name: 'Sterlin Diş Tedavileri', icon: '🦷', currency: '£', dbCurrency: 'GBP', dbName: 'Diş' },
  { id: 'dental-eur', name: 'Euro Diş Tedavileri', icon: '🦷', currency: '€', dbCurrency: 'EUR', dbName: 'Diş' },
  { id: 'aesthetic', name: 'Estetik Fiyat Listesi', icon: '💉', currency: '€', dbCurrency: 'EUR', dbName: 'Estetik' },
  { id: 'hotel', name: 'Otel Fiyat Listesi', icon: '🏨', currency: '₺', dbCurrency: null, dbName: 'Otel' },
];

export default function PriceLists() {
  const { getAuthHeader, isAdmin } = useAuth();
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [selectedTab, setSelectedTab] = useState('dental-gbp');

  const fetchPriceLists = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/price-lists`, getAuthHeader());
      setPriceLists(response.data);
    } catch (error) {
      console.error('Error fetching price lists:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchPriceLists();
  }, [fetchPriceLists]);

  // Get current price list by tab
  const getCurrentPriceList = () => {
    const category = PRICE_LIST_CATEGORIES.find(c => c.id === selectedTab);
    if (!category) return null;
    
    return priceLists.find(pl => {
      const nameMatch = pl.name.includes(category.dbName);
      if (category.dbCurrency) {
        return nameMatch && (pl.currency === category.dbCurrency || pl.currency?.includes(category.dbCurrency));
      }
      return nameMatch;
    });
  };

  const currentPriceList = getCurrentPriceList();

  // Handle image upload
  const handleImageUpload = async (file) => {
    if (!currentPriceList) {
      toast.error('Fiyat listesi bulunamadı');
      return;
    }

    setUploadingImage(selectedTab);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API_URL}/api/price-lists/${currentPriceList.id}/upload-image`,
        formData,
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      toast.success('Görsel yüklendi');
      fetchPriceLists();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Görsel yüklenemedi');
    } finally {
      setUploadingImage(null);
    }
  };

  // Handle image delete
  const handleDeleteImage = async () => {
    if (!currentPriceList) return;
    
    if (!window.confirm('Görseli silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.put(
        `${API_URL}/api/price-lists/${currentPriceList.id}`,
        { image_url: null },
        getAuthHeader()
      );
      toast.success('Görsel silindi');
      fetchPriceLists();
    } catch (error) {
      toast.error('Görsel silinemedi');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="price-lists-loading">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Staff görünümü - Kategori seçimli görsel gösterimi
  if (!isAdmin()) {
    // Get price list for selected category
    const getStaffPriceList = () => {
      const category = PRICE_LIST_CATEGORIES.find(c => c.id === selectedTab);
      if (!category) return null;
      
      return priceLists.find(pl => {
        const nameMatch = pl.name.includes(category.dbName);
        if (category.dbCurrency) {
          return nameMatch && (pl.currency === category.dbCurrency || pl.currency?.includes(category.dbCurrency));
        }
        return nameMatch;
      });
    };
    
    const staffCurrentPriceList = getStaffPriceList();
    
    return (
      <div className="space-y-6" data-testid="price-lists-page-staff">
        {/* Header with Category Tabs */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-8 text-white">
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          
          <div className="relative z-10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold font-[Manrope] flex items-center gap-3">
                <Crown className="h-8 w-8" />
                Fiyat Listeleri
              </h1>
              <p className="mt-2 text-white/80">Güncel fiyat listeleri</p>
            </div>
            
            {/* Category Tabs for Staff */}
            <div className="flex flex-wrap gap-2 bg-white/20 p-2 rounded-xl backdrop-blur">
              {PRICE_LIST_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedTab(cat.id)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
                    selectedTab === cat.id 
                      ? 'bg-white text-purple-600 shadow-lg' 
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  {cat.icon} {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Selected Category Image */}
        <Card>
          <CardHeader className="pb-2 bg-gradient-to-r from-blue-50 to-purple-50">
            <CardTitle className="font-[Manrope] flex items-center gap-2">
              {PRICE_LIST_CATEGORIES.find(c => c.id === selectedTab)?.icon}
              {PRICE_LIST_CATEGORIES.find(c => c.id === selectedTab)?.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {staffCurrentPriceList?.image_url ? (
              <div className="flex justify-center">
                <img 
                  src={staffCurrentPriceList.image_url} 
                  alt={`${staffCurrentPriceList.name} Fiyat Listesi`} 
                  className="max-w-full rounded-lg shadow-lg"
                  style={{ maxHeight: '80vh' }}
                />
              </div>
            ) : (
              <div className="text-center py-12">
                <Image className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Görsel Bulunamadı</h3>
                <p className="text-muted-foreground">Bu kategori için henüz görsel yüklenmemiş</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin görünümü - Görsel yükleme
  return (
    <div className="space-y-6" data-testid="price-lists-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-8 text-white">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <div className="relative z-10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold font-[Manrope] flex items-center gap-3">
              <Crown className="h-8 w-8" />
              Fiyat Listeleri
            </h1>
            <p className="mt-2 text-white/80">Her kategori için görsel yükleyin</p>
          </div>
          
          {/* Tab Toggle */}
          <div className="flex flex-wrap gap-2 bg-white/20 p-2 rounded-xl backdrop-blur">
            {PRICE_LIST_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedTab(cat.id)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
                  selectedTab === cat.id 
                    ? 'bg-white text-purple-600 shadow-lg' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Yenile butonu */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={fetchPriceLists}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Yenile
        </Button>
      </div>

      {/* Görsel Yükleme Alanı */}
      <Card>
        <CardHeader>
          <CardTitle className="font-[Manrope] flex items-center gap-2">
            {PRICE_LIST_CATEGORIES.find(c => c.id === selectedTab)?.icon}
            {PRICE_LIST_CATEGORIES.find(c => c.id === selectedTab)?.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currentPriceList?.image_url ? (
            <div className="space-y-4">
              {/* Mevcut görsel */}
              <div className="border rounded-lg p-4 bg-muted/20">
                <img 
                  src={currentPriceList.image_url} 
                  alt={`${currentPriceList.name} Fiyat Listesi`} 
                  className="max-w-full mx-auto rounded-lg shadow-lg"
                  style={{ maxHeight: '600px' }}
                />
              </div>
              
              {/* Kontrol butonları */}
              <div className="flex gap-3 justify-end">
                <Button 
                  variant="outline" 
                  onClick={handleDeleteImage}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Görseli Sil
                </Button>
                <input
                  type="file"
                  id="change-image"
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file);
                    e.target.value = '';
                  }}
                />
                <Button
                  onClick={() => document.getElementById('change-image').click()}
                  disabled={uploadingImage === selectedTab}
                  className="bg-gradient-to-r from-blue-600 to-purple-600"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadingImage === selectedTab ? 'Yükleniyor...' : 'Yeni Görsel Yükle'}
                </Button>
              </div>
            </div>
          ) : (
            <div 
              className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => document.getElementById('upload-image').click()}
            >
              <Image className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Görsel Yükle</h3>
              <p className="text-muted-foreground mb-4">
                Bu kategori için fiyat listesi görseli yükleyin (PNG, JPG)
              </p>
              <input
                type="file"
                id="upload-image"
                accept=".png,.jpg,.jpeg,.webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageUpload(file);
                  e.target.value = '';
                }}
              />
              <Button
                disabled={uploadingImage === selectedTab}
                className="bg-gradient-to-r from-blue-600 to-purple-600"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadingImage === selectedTab ? 'Yükleniyor...' : 'Görsel Seç'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tüm kategorilerin durumu */}
      <Card>
        <CardHeader>
          <CardTitle className="font-[Manrope] text-lg">Kategori Durumu</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PRICE_LIST_CATEGORIES.map(cat => {
              const pl = priceLists.find(p => {
                const nameMatch = p.name.includes(cat.dbName);
                if (cat.dbCurrency) {
                  return nameMatch && (p.currency === cat.dbCurrency || p.currency?.includes(cat.dbCurrency));
                }
                return nameMatch;
              });
              const hasImage = pl?.image_url;
              
              return (
                <div 
                  key={cat.id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedTab === cat.id 
                      ? 'border-purple-500 bg-purple-50' 
                      : hasImage 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-gray-200 bg-gray-50'
                  }`}
                  onClick={() => setSelectedTab(cat.id)}
                >
                  <div className="text-center">
                    <span className="text-2xl">{cat.icon}</span>
                    <p className="text-sm font-medium mt-2">{cat.name}</p>
                    <div className={`mt-2 text-xs font-semibold ${hasImage ? 'text-green-600' : 'text-gray-400'}`}>
                      {hasImage ? '✓ Görsel Var' : '○ Görsel Yok'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
